"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import DepartureDateRowMobile from "@/components/product-detail-page/dates-and-pricing/departure-date-row-mobile";
import DepartureDateRowDesktop from "@/components/product-detail-page/dates-and-pricing/departure-date-row-desktop";

interface DepartureDateRowProps {
  index: number;
  tourDate: TourDate;
  isSelected: boolean;
  onSelect: (tourDate: TourDate) => void;
  onRequestQuote?: (tourDate: TourDate) => void;
  onViewItinerary?: (tourDate: TourDate) => void;
  onBookNow?: (tourDate: TourDate) => void;
  onHover?: (tourDate: TourDate) => void;
  lastItem: boolean;
}

const DepartureDateRow = ({
  index,
  tourDate,
  isSelected,
  onSelect,
  onRequestQuote,
  onViewItinerary,
  onBookNow,
  onHover,
  lastItem
}: DepartureDateRowProps) => {
  const t = useTranslations("PDP");

  const formatDateRange = (startDate: string, endDate: string) => {
    try {
      // Parse ISO date strings (YYYY-MM-DD) without timezone issues
      const startDateObj = new Date(`${startDate}T00:00:00`);
      const endDateObj = new Date(`${endDate}T00:00:00`);

      const startFormatted = startDateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      });

      const endFormatted = endDateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });

      return `${startFormatted} - ${endFormatted}`;
    } catch {
      return `${startDate} - ${endDate}`;
    }
  };
  
  const hasSpecialEvent = tourDate.specialDates.some((sd) => sd.title === "Special Event");
  const hasReverseItinerary = tourDate.itineraryType === "Reverse";
  const isSoldOut = tourDate.statusId === 3 || tourDate.statusMessage === "Sold Out";
  const isCallToBook = !!tourDate.callForDetailsPhoneNumber && tourDate.callForDetails;
  const isSeatsRemainingThreshold = !!tourDate.statusMessage  && tourDate.seatsRemainingBelowThreshold;
  const showStatusMessage = (!(isCallToBook && isSelected) || isSeatsRemainingThreshold);

  // Calculate offer amount if present
  const calculateOfferAmount = () => {
    if (!tourDate.hasOffer || !tourDate.strikeOutPrice || !tourDate.offerPrice) return "";

    const original = parseFloat(tourDate.strikeOutPrice.replace(/[$,]/g, ""));
    const offer = parseFloat(tourDate.offerPrice.replace(/[$,]/g, ""));
    const savings = original - offer;

    return savings > 0 ? `${t("save")} $${savings.toFixed(0)}` : "";
  };

  const offerBadgeText = calculateOfferAmount();

  const formattedDate = formatDateRange(tourDate.startDate, tourDate.endDate);

  return (
    <>
      <DepartureDateRowMobile
        index={index}
        hasSpecialEvent={hasSpecialEvent}
        hasReverseItinerary={hasReverseItinerary}
        isSoldOut={isSoldOut}
        isCallToBook={isCallToBook}
        isSeatsRemainingThreshold={isSeatsRemainingThreshold}
        showStatusMessage={showStatusMessage}
        formattedDate={formattedDate}
        offerBadgeText={offerBadgeText}
        hasOffer={tourDate.hasOffer}
        tourDate={tourDate}
        isSelected={isSelected}
        onSelect={onSelect}
        lastItem={lastItem}
        onHover={onHover}
        onBookNow={onBookNow}
        onRequestQuote={onRequestQuote}
        onViewItinerary={onViewItinerary}
      />
       <DepartureDateRowDesktop
        index={index}
        hasSpecialEvent={hasSpecialEvent}
        hasReverseItinerary={hasReverseItinerary}
        isSoldOut={isSoldOut}
        isCallToBook={isCallToBook}
        isSeatsRemainingThreshold={isSeatsRemainingThreshold}
        showStatusMessage={showStatusMessage}
        formattedDate={formattedDate}
        offerBadgeText={offerBadgeText}
        hasOffer={tourDate.hasOffer}
        tourDate={tourDate}
        isSelected={isSelected}
        onSelect={onSelect}
        lastItem={lastItem}
        onHover={onHover}
        onBookNow={onBookNow}
        onRequestQuote={onRequestQuote}
        onViewItinerary={onViewItinerary}
       />
    </>
  );
};

export default DepartureDateRow;
